import { useParams, useNavigate, Link } from 'react-router-dom'
import { Facebook, Twitter, Linkedin, Link as LinkIcon, ChevronLeft, ChevronRight } from 'lucide-react'
import Breadcrumb from '@/components/ui/Breadcrumb'
import Newsletter from '@/components/sections/Newsletter'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { useHistory, useHistories } from '@/lib/api/hooks/history'
import type { History } from '@/lib/api/types'
import { motion } from 'framer-motion'
import { fadeUp, staggerContainer, viewportReveal } from '@/lib/motion'
import { parseHtmlString } from '@/utils/helpers/html'

// Helper function to format date
const formatDate = (dateString: string | null): string => {
    if (!dateString) return ''
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
}

export default function HistoryDetail() {
    const navigate = useNavigate()
    const { historyId } = useParams<{ historyId: string }>()

    const { data: historyData, isLoading, error } = useHistory(historyId)
    const { data: otherHistoriesData } = useHistories({})

    const apiHistory = historyData?.data
    const allHistories = otherHistoriesData?.data?.data || []

    // Find current history index for navigation
    const currentHistoryIndex = allHistories.findIndex((h: History) => h.historyId === historyId)
    const hasPrevious = currentHistoryIndex > 0
    const hasNext = currentHistoryIndex >= 0 && currentHistoryIndex < allHistories.length - 1
    const previousHistory = hasPrevious ? allHistories[currentHistoryIndex - 1] : null
    const nextHistory = hasNext ? allHistories[currentHistoryIndex + 1] : null

    // Get other histories for sidebar
    const otherHistories = allHistories
        .filter((h: History) => h.historyId !== historyId)
        .slice(0, 3)

    const handleShare = (platform: string) => {
        if (!apiHistory) return

        const url = encodeURIComponent(window.location.href)
        const title = encodeURIComponent(`${apiHistory.title} - African Space Agency`)

        switch (platform) {
            case 'facebook':
                window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank')
                break
            case 'twitter':
                window.open(`https://twitter.com/intent/tweet?url=${url}&text=${title}`, '_blank')
                break
            case 'linkedin':
                window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank')
                break
            case 'copy':
                navigator.clipboard.writeText(window.location.href)
                alert('Link copied to clipboard!')
                break
            default:
                navigator.clipboard.writeText(window.location.href)
        }
    }

    if (isLoading) {
        return <LoadingSpinner fullScreen message="Loading history..." />
    }

    if (error || !apiHistory) {
        return (
            <div className="bg-white min-h-screen">
                <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 pb-16">
                    <div className="text-center py-12">
                        <p className="text-red-600 mb-4">History not found or error loading history.</p>
                        <button
                            onClick={() => navigate('/about/history')}
                            className="px-4 py-2 bg-wine-100 text-white rounded hover:bg-wine-200 transition-colors"
                        >
                            Back to History
                        </button>
                    </div>
                </div>
            </div>
        )
    }

    const breadcrumbItems = [
        { label: 'Home', href: '/' },
        { label: 'About Us', href: '/about' },
        { label: 'Our History', href: '/about/history' },
        { label: apiHistory.title, href: '#', active: true }
    ]

    return (
        <motion.div
            className="bg-white min-h-screen"
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
        >
            <motion.div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 pb-16" variants={fadeUp}>
                <div className="mb-5">
                    <Breadcrumb items={breadcrumbItems} />
                </div>

                <motion.div
                    className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-14"
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="visible"
                    viewport={viewportReveal}
                >
                    {/* Main Content */}
                    <div className="lg:col-span-2 order-1">
                        <div className="space-y-8">
                            {/* History Image */}
                            <img
                                src={apiHistory.imageUrl}
                                alt={apiHistory.title}
                                className="w-full h-[300px] md:h-[400px] object-cover rounded-lg"
                            />

                            {/* Year Badge and Title */}
                            <div className="space-y-4">
                                <div className="flex items-center gap-4">
                                    <span className="inline-flex items-center px-4 py-1.5 rounded-full text-sm font-medium bg-wine-100 text-white">
                                        {apiHistory.year}
                                    </span>
                                    {apiHistory.datePublished && (
                                        <span className="text-base font-normal font-satoshi text-gray-500">
                                            {formatDate(apiHistory.datePublished)}
                                        </span>
                                    )}
                                </div>

                                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-normal font-['PT_Serif'] text-gray-900 leading-tight">
                                    {apiHistory.title}
                                </h1>
                            </div>

                            {/* Description */}
                            <div className="prose prose-lg max-w-none">
                                <p className="text-lg font-normal font-satoshi text-gray-700 leading-relaxed whitespace-pre-line">
                                    <span dangerouslySetInnerHTML={parseHtmlString(apiHistory.description)} />
                                </p>
                            </div>

                            {/* Social Share */}
                            <div className="space-y-4 pt-6 border-t border-gray-200">
                                <span className="text-lg font-normal font-['PT_Serif'] text-gray-600">
                                    SHARE
                                </span>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => handleShare('facebook')}
                                        className="w-6 h-6 text-gray-800 hover:text-forestGreen-100 transition-colors"
                                        aria-label="Share on Facebook"
                                    >
                                        <Facebook className="w-full h-full" />
                                    </button>
                                    <button
                                        onClick={() => handleShare('twitter')}
                                        className="w-6 h-6 text-gray-800 hover:text-forestGreen-100 transition-colors"
                                        aria-label="Share on Twitter"
                                    >
                                        <Twitter className="w-full h-full" />
                                    </button>
                                    <button
                                        onClick={() => handleShare('linkedin')}
                                        className="w-6 h-6 text-gray-800 hover:text-forestGreen-100 transition-colors"
                                        aria-label="Share on LinkedIn"
                                    >
                                        <Linkedin className="w-full h-full" />
                                    </button>
                                    <button
                                        onClick={() => handleShare('copy')}
                                        className="w-6 h-6 text-gray-800 hover:text-forestGreen-100 transition-colors"
                                        aria-label="Copy link"
                                    >
                                        <LinkIcon className="w-full h-full" />
                                    </button>
                                </div>
                            </div>

                            {/* Navigation */}
                            {!!(hasPrevious || hasNext) && (
                                <div className="flex items-center justify-between gap-4 pt-4 border-t border-gray-200">
                                    <button
                                        onClick={() => previousHistory && navigate(`/about/history/${previousHistory.historyId}`)}
                                        disabled={!hasPrevious}
                                        className={`flex items-center gap-2 px-4 py-2 rounded transition-colors ${hasPrevious
                                            ? 'text-gray-900 hover:bg-gray-200'
                                            : 'text-gray-400 cursor-not-allowed'
                                            }`}
                                    >
                                        <ChevronLeft className="w-5 h-5" />
                                        <span className="text-sm font-medium font-satoshi">Previous</span>
                                    </button>

                                    <button
                                        onClick={() => nextHistory && navigate(`/about/history/${nextHistory.historyId}`)}
                                        disabled={!hasNext}
                                        className={`flex items-center gap-2 px-4 py-2 rounded transition-colors ${hasNext
                                            ? 'text-gray-900 hover:bg-gray-200'
                                            : 'text-gray-400 cursor-not-allowed'
                                            }`}
                                    >
                                        <span className="text-sm font-medium font-satoshi">Next</span>
                                        <ChevronRight className="w-5 h-5" />
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Sidebar */}
                    <div className="lg:col-span-1 order-2">
                        <div className="space-y-10">
                            {/* Other History Items */}
                            <div className="space-y-6">
                                <h3 className="text-xl sm:text-2xl font-normal font-['PT_Serif'] text-gray-900 leading-7">
                                    MORE HISTORY
                                </h3>

                                <div className="space-y-4">
                                    {otherHistories.length > 0 ? (
                                        otherHistories.map((history: History) => (
                                            <Link
                                                key={history.historyId}
                                                to={`/about/history/${history.historyId}`}
                                                className="block bg-gray-50 hover:bg-wine-100 transition-colors overflow-hidden group"
                                            >
                                                <img
                                                    src={history.imageUrl}
                                                    alt={history.title}
                                                    className="w-full h-32 object-cover"
                                                />
                                                <div className="p-4 space-y-2">
                                                    <div className="flex items-center gap-2">
                                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-wine-100 text-white group-hover:bg-white group-hover:text-wine-100 transition-colors">
                                                            {history.year}
                                                        </span>
                                                    </div>
                                                    <p className="text-base font-medium font-satoshi text-gray-900 leading-normal line-clamp-2 group-hover:text-white transition-colors">
                                                        {history.title}
                                                    </p>
                                                </div>
                                            </Link>
                                        ))
                                    ) : (
                                        <p className="text-gray-600 text-sm">No other history items available.</p>
                                    )}
                                </div>
                            </div>

                            {/* Newsletter */}
                            <Newsletter />
                        </div>
                    </div>
                </motion.div>
            </motion.div>
        </motion.div>
    )
}
